import { getDisplayedName, getProductId } from 'features/product-info';
import React from 'react';
import { connect } from 'react-redux';
import type { Dispatch, Action } from 'redux';
import { RootReducer } from 'store/root-reducer';
import { LayoutControlledTypography } from 'common/components/fronton-decorators';
import { CheckCircleIcon, CopyIcon, IconComponent } from '@fronton/icons-react';
import { Icon, Snack, SnackVariant, Snackbar, useSnackbar, Marker } from 'fronton-react';
import { analyticsUtils } from 'utils';
import { MarkerLayout, styles } from './styles';
import { selectLoyaltyRewardData } from '../../../product-loyalty-rewards/selectors/loyalty-rewards';
import { LoyaltyRewardsReducer } from '../../../product-loyalty-rewards/types/reducer';
import { BonusIcon } from 'common/icons/bonus-icon';

export const DATA_QA_COPY_BUTTON = 'copy-button';
export const DATA_QA_PRODUCT_NAME = 'product-name';
export const DATA_QA_LOYALTY_REWARDS_MARKER = 'loyalty-rewards-marker';

type ProductTitleMappedState = {
  productId: string;
  displayedName: string;
  loyaltyRewards: LoyaltyRewardsReducer.ILoyaltyReward | null;
};

type SnackData = {
  id: string;
  title: string;
  picture: React.ReactElement<IconComponent>;
  type: SnackVariant;
};

export type ProductTitleProps = {
  Htag?: React.ComponentType<React.HTMLAttributes<HTMLElement>> | keyof JSX.IntrinsicElements;
} & ProductTitleMappedState &
  React.HTMLAttributes<HTMLElement>;

export const ProductTitle: React.FC<ProductTitleProps> = ({
  productId,
  displayedName,
  loyaltyRewards,
  Htag = 'h1',
  className,
  ...htmlProps
}) => {
  const data: SnackData[] = [
    {
      id: 'snack-copy',
      title: 'Артикул скопирован',
      picture: <CheckCircleIcon />,
      type: 'neutral',
    },
  ];
  const { state, addSnack } = useSnackbar();
  const skuText = `ЛМ ${productId}`;
  const copyClick = () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementBlock: 'productDetail',
      elementName: 'copySKU',
      elementText: skuText,
      elementType: 'button',
    });
    navigator.clipboard.writeText(productId);
    addSnack(data[0]);
  };
  return (
    <div className={className} {...htmlProps}>
      <LayoutControlledTypography as="div" variant="caption" className={styles.articleContainer}>
        {skuText}{' '}
        <CopyIcon
          data-qa={DATA_QA_COPY_BUTTON}
          size="s"
          className={styles.copyIconClass}
          onClick={copyClick}
        />
      </LayoutControlledTypography>
      {loyaltyRewards && (
        <MarkerLayout>
          <Marker
            data-qa={DATA_QA_LOYALTY_REWARDS_MARKER}
            iconLeft={<BonusIcon />}
            background="secondary-red-dark"
            variant="angular"
            color="text-primary-invert"
            size="l"
            text={loyaltyRewards.label}
          />
        </MarkerLayout>
      )}
      <LayoutControlledTypography
        as={Htag}
        variant="m"
        size="body_accent"
        className={styles.displayedNameContainer}
        data-qa={DATA_QA_PRODUCT_NAME}
      >
        {displayedName.slice(0, 120)}
      </LayoutControlledTypography>
      <Snackbar horizontalPosition="center">
        {state.map(({ id, title, picture, type }) => (
          <Snack
            key={id}
            id={id}
            data-qa={id}
            header={title}
            icon={<Icon size={24}>{picture}</Icon>}
            variant={type}
            autoHideTimer={1000}
          ></Snack>
        ))}
      </Snackbar>
    </div>
  );
};

export const mapStoreToProps = (store: RootReducer): ProductTitleMappedState => {
  const productId = getProductId(store) ?? '';
  const displayedName = getDisplayedName(store) ?? '';
  const loyaltyRewards = selectLoyaltyRewardData(store) ?? null;

  return {
    productId,
    displayedName,
    loyaltyRewards,
  };
};

export const PdpProductTitle = connect(mapStoreToProps)(
  ({
    dispatch,
    ...restProps
  }: { dispatch: Dispatch<Action> } & React.ComponentProps<typeof ProductTitle>) => (
    <ProductTitle {...restProps} />
  ),
);
